import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Collapse,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DriveFileRenameOutlineOutlinedIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  getAllItemTypes,
  getCategoriesByTypeId,
  getItemsByCategoryId,
} from "../../services/InventorySettings";
import AddEditType from "../../components/settings/AddEditType";
import AddEditCategory from "../../components/settings/AddEditCategory";
import AddEditItem from "../../components/settings/AddEditItem";

const InventorySettings = () => {
  const [data, setData] = useState<any[]>([]);
  const [expanded, setExpanded] = useState<{ [key: string]: boolean }>({});
  const [loading, setLoading] = useState(false);
  const [categoryLoading, setCategoryLoading] = useState<{ [key: string]: boolean }>({});
  const [itemLoading, setItemLoading] = useState<{ [key: string]: boolean }>({});
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success",
  });

  // Dialog states
  const [typeDialog, setTypeDialog] = useState({ open: false, editData: null });
  const [categoryDialog, setCategoryDialog] = useState({ open: false, editData: null, typeId: 0 });
  const [itemDialog, setItemDialog] = useState({ open: false, editData: null, categoryId: 0, typeId: 0 });

  const rowStyle = {
    display: "grid",
    gridTemplateColumns: "350px 110px 110px 110px 110px 110px",
    alignItems: "center",
    width: "100%",
    gap: "8px",
  };

  const headerStyle = {
    display: "grid",
    gridTemplateColumns: "440px 110px 110px 110px 110px 110px",
    alignItems: "center",
    textAlign: "center" as const,
    fontWeight: 600,
    bgcolor: "grey.100",
    borderBottom: "1px solid",
    borderColor: "grey.100",
    p: 1,
    mb: 1,
    gap: "8px",
  };

  const load = async () => {
    try {
      setLoading(true);
      const result = await getAllItemTypes();
      setData(Array.isArray(result) ? result : []);
    } catch (err) {
      console.error("Load failed:", err);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Clean up dialog data after closing
  useEffect(() => {
    if (!typeDialog.open) {
      const timer = setTimeout(() => {
        setTypeDialog(prev => ({ ...prev, editData: null }));
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [typeDialog.open]);

  useEffect(() => {
    if (!categoryDialog.open) {
      const timer = setTimeout(() => {
        setCategoryDialog(prev => ({ ...prev, editData: null, typeId: 0 }));
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [categoryDialog.open]);

  useEffect(() => {
    if (!itemDialog.open) {
      const timer = setTimeout(() => {
        setItemDialog(prev => ({ ...prev, editData: null, categoryId: 0, typeId: 0 }));
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [itemDialog.open]);

  const loadCategories = async (typeId: number) => {
    try {
      setCategoryLoading({ ...categoryLoading, [typeId]: true });
      const categories = await getCategoriesByTypeId(typeId);
      
      setData(prevData => 
        prevData.map(type => 
          type.id === typeId 
            ? { ...type, categories: Array.isArray(categories) ? categories : [] }
            : type
        )
      );
    } catch (err) {
      console.error("Load categories failed:", err);
    } finally {
      setCategoryLoading(prev => ({ ...prev, [typeId]: false }));
    }
  };

  const loadItems = async (categoryId: number, typeId: number) => {
    try {
      setItemLoading({ ...itemLoading, [categoryId]: true });
      const items = await getItemsByCategoryId(categoryId);
      
      setData(prevData => 
        prevData.map(type => 
          type.id === typeId 
            ? {
                ...type,
                categories: type.categories?.map((cat: any) => 
                  cat.id === categoryId 
                    ? { ...cat, items: Array.isArray(items) ? items : [] }
                    : cat
                )
              }
            : type
        )
      );
    } catch (err) {
      console.error("Load items failed:", err);
    } finally {
      setItemLoading(prev => ({ ...prev, [categoryId]: false }));
    }
  };

  const handleTypeExpand = async (typeId: number) => {
    const isExpanding = !expanded[`type-${typeId}`];
    setExpanded({ ...expanded, [`type-${typeId}`]: isExpanding });
    
    if (isExpanding) {
      const type = data.find(t => t.id === typeId);
      if (!type?.categories) {
        await loadCategories(typeId);
      }
    }
  };

  const handleCategoryExpand = async (categoryId: number, typeId: number) => {
    const isExpanding = !expanded[`cat-${categoryId}`];
    setExpanded({ ...expanded, [`cat-${categoryId}`]: isExpanding });
    
    if (isExpanding) {
      const type = data.find(t => t.id === typeId);
      const category = type?.categories?.find((c: any) => c.id === categoryId);
      if (!category?.items) {
        await loadItems(categoryId, typeId);
      }
    }
  };

  const handleSuccess = async (message: string) => {
    setSnackbar({ open: true, message, severity: "success" });
    
    // Store current expanded state
    const currentExpanded = { ...expanded };
    const expandedTypes = Object.keys(currentExpanded)
      .filter(key => key.startsWith('type-') && currentExpanded[key])
      .map(key => parseInt(key.replace('type-', '')));
    
    const expandedCategories = Object.keys(currentExpanded)
      .filter(key => key.startsWith('cat-') && currentExpanded[key])
      .map(key => parseInt(key.replace('cat-', '')));
    
    // Reload types first
    await load();
    
    // Restore expanded state
    setExpanded(currentExpanded);
    
    // Reload categories for expanded types (in parallel)
    const categoryPromises = expandedTypes.map(async (typeId) => {
      await loadCategories(typeId);
    });
    await Promise.all(categoryPromises);
    
    // Reload items for expanded categories (in parallel)
    if (expandedCategories.length > 0) {
      const itemPromises = expandedCategories.map(async (categoryId) => {
        // Find the typeId for this category
        const type = data.find(t => t.categories?.some((c: any) => c.id === categoryId));
        if (type) {
          await loadItems(categoryId, type.id);
        }
      });
      await Promise.all(itemPromises);
    }
  };

  const openAddType = () => {
    setTypeDialog({ open: true, editData: null });
  };

  const openEditType = (type: any) => {
    setTypeDialog({ open: true, editData: type });
  };

  const openAddCategory = (typeId: number) => {
    setCategoryDialog({ open: true, editData: null, typeId });
  };

  const openEditCategory = (cat: any) => {
    setCategoryDialog({ open: true, editData: cat, typeId: cat.typeId });
  };

  const openAddItem = (categoryId: number, typeId: number) => {
    setItemDialog({ open: true, editData: null, categoryId, typeId });
  };

  const openEditItem = (item: any) => {
    setItemDialog({ open: true, editData: item, categoryId: item.categoryId, typeId: item.typeId });
  };

  return (
    <Box sx={{ p:1, height: "calc(100vh - 3.5rem)", display: "flex", flexDirection: "column" }}>
      <Paper sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", p:1 }}>
        <Box sx={headerStyle}>
          <Typography fontWeight={600} sx={{ textAlign: "left" }}>Structure Name</Typography>
          <Typography fontWeight={600}>Weight</Typography>
          <Typography fontWeight={600} color="#000">Item Cost</Typography>
          <Typography fontWeight={600} color="#000">Selling Price</Typography>
          <Typography fontWeight={600}>Stock alert</Typography>
          <Typography fontWeight={600}>In Stock</Typography>
        </Box>

        <Button
          size="small"
          onClick={openAddType}
          disableRipple
          sx={{ textTransform: "none", color: "primary.main", fontWeight: 600, fontSize: "14px", alignSelf: "flex-start",  pl: 2, "&:hover": { backgroundColor: "transparent" }, "&:active": { backgroundColor: "transparent" } }}
          endIcon={<AddIcon />}
        >
          Add Type
        </Button>

        <Box sx={{ flex: 1, overflow: "auto", "&::-webkit-scrollbar": { width: "8px" }, "&::-webkit-scrollbar-track": { background: "transparent" }, "&::-webkit-scrollbar-thumb": { background: "#CBD5E1", borderRadius: "4px" } }}>
          {!loading && Array.isArray(data) && data.map((type) => (
            <Box key={type.id}>
              <Box sx={{ ...rowStyle, cursor: "pointer", p: 1, pl:2 }} onClick={() => handleTypeExpand(type.id)}>
                <Box display="flex" alignItems="center" sx={{ fontWeight: 600, fontSize: "16px", color: "grey.500" }}>
                  <IconButton size="small" sx={{ mr: 1 }}>
                    {categoryLoading[type.id] ? (
                      <CircularProgress size={16} />
                    ) : expanded[`type-${type.id}`] ? (
                      <ExpandMoreIcon />
                    ) : (
                      <ChevronRightIcon />
                    )}
                  </IconButton>
                  <Typography fontWeight={600}>{type.typeName}</Typography>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditType(type); }} sx={{ ml: 1 }}>
                    <DriveFileRenameOutlineOutlinedIcon fontSize="small" sx={{ fontWeight: 600, fontSize: "20px", color: "primary.main" }} />
                  </IconButton>
                </Box>
              </Box>
              <Collapse in={expanded[`type-${type.id}`]}>
                <Box sx={{ pl: 6 }}>
                  <Button size="small" disableRipple endIcon={<AddIcon />} sx={{ textTransform: "none", color: "primary.main", fontWeight: 600, fontSize: "14px", "&:hover": { backgroundColor: "transparent" }, "&:active": { backgroundColor: "transparent" } }} onClick={() => openAddCategory(type.id)}>
                    Add Category
                  </Button>
                  {type.categories?.map((cat: any) => (
                    <Box key={cat.id}>
                      <Box sx={{ ...rowStyle, cursor: "pointer", p: 1 }} onClick={() => handleCategoryExpand(cat.id, type.id)}>
                        <Box display="flex" alignItems="center" sx={{ fontWeight: 600, fontSize: "16px", color: "grey.500" }}>
                          <IconButton size="small" sx={{ mr: 1 }}>
                            {itemLoading[cat.id] ? (
                              <CircularProgress size={16} />
                            ) : expanded[`cat-${cat.id}`] ? (
                              <ExpandMoreIcon />
                            ) : (
                              <ChevronRightIcon />
                            )}
                          </IconButton>
                          {cat.categoryName}
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditCategory(cat); }} sx={{ ml: 1 }}>
                            <DriveFileRenameOutlineOutlinedIcon fontSize="small" sx={{ fontWeight: 600, fontSize: "20px", color: "primary.main" }} />
                          </IconButton>
                        </Box>
                      </Box>
                      <Collapse in={expanded[`cat-${cat.id}`]}>
                        <Box sx={{ pl: 6 }}>
                          <Button size="small" disableRipple endIcon={<AddIcon />} sx={{ textTransform: "none", color: "primary.main", fontWeight: 600, fontSize: "14px", "&:hover": { backgroundColor: "transparent" }, "&:active": { backgroundColor: "transparent" } }} onClick={() => openAddItem(cat.id, type.id)}>
                            Add Item
                          </Button>
                          {cat.items?.map((item: any) => (
                            <Box key={item.id} sx={{ ...rowStyle, p: 1 }}>
                              <Box sx={{ fontWeight: 600, fontSize: "16px", color: "grey.500", display: "flex", alignItems: "center" }}>
                                <Typography fontWeight={600} color="grey.500">{item.itemName}</Typography>
                                <IconButton size="small" onClick={() => openEditItem(item)} sx={{ ml: 1 }}>
                                  <DriveFileRenameOutlineOutlinedIcon fontSize="small" sx={{ fontWeight: 600, fontSize: "20px", color: "primary.main" }} />
                                </IconButton>
                              </Box>
                              <Typography color="grey.500" sx={{ textAlign: "center", fontWeight: 600 }}>{item.weight} KG</Typography>
                              <Typography color="text.primary" sx={{ textAlign: "center", fontWeight: 600 }}>₹{item.cost || 0}</Typography>
                              <Typography color="text.primary" sx={{ textAlign: "center", fontWeight: 600 }}>₹{item.sellingPrice || 0}</Typography>
                              <Typography color="grey.500" sx={{ textAlign: "center", fontWeight: 600 }}>{item.stockAlert}</Typography>
                              <Typography color="grey.500" sx={{ textAlign: "center", fontWeight: 600 }}>{item.inStockCount || 0}</Typography>
                            </Box>
                          ))}
                        </Box>
                      </Collapse>
                    </Box>
                  ))}
                </Box>
              </Collapse>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Dialog Components */}
      <AddEditType
        open={typeDialog.open}
        onClose={() => setTypeDialog(prev => ({ ...prev, open: false }))}
        editData={typeDialog.editData}
        onSuccess={handleSuccess}
      />
      
      <AddEditCategory
        open={categoryDialog.open}
        onClose={() => setCategoryDialog(prev => ({ ...prev, open: false }))}
        editData={categoryDialog.editData}
        typeId={categoryDialog.typeId}
        onSuccess={handleSuccess}
      />
      
      <AddEditItem
        open={itemDialog.open}
        onClose={() => setItemDialog(prev => ({ ...prev, open: false }))}
        editData={itemDialog.editData}
        categoryId={itemDialog.categoryId}
        typeId={itemDialog.typeId}
        onSuccess={handleSuccess}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ 
            width: "100%",
            backgroundColor: `${snackbar.severity === "success" ? "success.main" : "error.main"} !important`,
            color: "white",
            "& .MuiAlert-icon": { color: "white" },
            "& .MuiAlert-action": { color: "white" }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InventorySettings;